
import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { AnimatedSection } from '../components/shared/AnimatedSection';
import { Button } from '../components/shared/Button';
import { FeedSection } from '../components/shared/FeedSection';
import { SocialEcosystem } from '../components/shared/SocialEcosystem';
import { RoadmapSection } from '../components/shared/RoadmapSection'; // Importazione
import { ConsultationSection } from '../components/shared/ConsultationSection';
import { ROUTES } from '../constants';
import { 
  TrendingUp, 
  BarChart3, 
  Layers, 
  CheckCircle2, 
  ArrowRight
} from 'lucide-react';

export const FinancePage: React.FC = () => {
  const { t } = useLanguage();

  return (
    <div className="bg-[#0B1120] text-white min-h-screen pt-32 pb-20">
      
      {/* 1. HERO SECTION */}
      <section className="mb-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <AnimatedSection>
            <h1 className="text-5xl md:text-7xl font-black uppercase tracking-tight mb-6">
              <span className="text-white">{t('finance.heroTitle')}</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
               {t('finance.heroDesc')}
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* 2. SERVIZI ATTIVI SECTION */}
      <section className="px-4 sm:px-6 lg:px-8 mb-24">
        <div className="max-w-7xl mx-auto">
          
          {/* Section Header */}
          <AnimatedSection className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="bg-[#151E32] text-green-500 border border-green-500/30 text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-widest shadow-[0_0_15px_rgba(34,197,94,0.2)]">
                {t('finance.badgeOperative')}
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black uppercase tracking-wide">
              {t('finance.activeServicesTitle')} <span className="text-orange-500">{t('finance.activeServicesSubtitle')}</span>
            </h2>
          </AnimatedSection>

          {/* Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            
            {/* CARD 1: BLUE STRIKE */}
            <AnimatedSection delay={100}>
              <div className="bg-[#151E32] border border-blue-500/30 rounded-2xl p-8 h-full flex flex-col relative group hover:border-blue-500 hover:shadow-[0_0_30px_rgba(59,130,246,0.15)] transition-all duration-300">
                <div className="absolute top-6 right-6">
                  <span className="flex items-center bg-green-500 text-white text-[10px] font-bold px-2 py-1 rounded shadow-lg">
                    <span className="w-1.5 h-1.5 bg-white rounded-full mr-1.5 animate-pulse"></span>
                    {t('common.live')}
                  </span>
                </div>
                <div className="w-14 h-14 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-500 mb-6 group-hover:scale-110 transition-transform duration-300">
                  <TrendingUp size={28} />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">{t('finance.services.blueStrike.title')}</h3>
                <p className="text-sm text-gray-400 mb-8 min-h-[40px]">
                  {t('finance.services.blueStrike.subtitle')}
                </p>
                <ul className="space-y-4 mb-8 flex-grow">
                  <li className="flex items-start text-sm text-gray-300">
                    <CheckCircle2 className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0" />
                    {t('finance.services.blueStrike.f1')}
                  </li>
                  <li className="flex items-start text-sm text-gray-300">
                    <CheckCircle2 className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0" />
                    {t('finance.services.blueStrike.f2')}
                  </li>
                  <li className="flex items-start text-sm text-gray-300">
                    <CheckCircle2 className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0" />
                    {t('finance.services.blueStrike.f3')}
                  </li>
                </ul>
                <Link to={ROUTES.BLUE_STRIKE} className="block w-full">
                  <Button className="w-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white border-0 shadow-lg shadow-blue-500/20">
                    {t('common.learnMore')} <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </AnimatedSection>

            {/* CARD 2: ALGO ENGINE */}
            <AnimatedSection delay={200}>
              <div className="bg-[#151E32] border border-orange-500/30 rounded-2xl p-8 h-full flex flex-col relative group hover:border-orange-500 hover:shadow-[0_0_30px_rgba(249,115,22,0.15)] transition-all duration-300">
                <div className="absolute top-6 right-6">
                  <span className="flex items-center bg-green-500 text-white text-[10px] font-bold px-2 py-1 rounded shadow-lg">
                    <span className="w-1.5 h-1.5 bg-white rounded-full mr-1.5 animate-pulse"></span>
                    {t('common.live')}
                  </span>
                </div>
                <div className="w-14 h-14 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center text-orange-500 mb-6 group-hover:scale-110 transition-transform duration-300">
                  <BarChart3 size={28} />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">{t('finance.services.algoEngine.title')}</h3>
                <p className="text-sm text-gray-400 mb-8 min-h-[40px]">
                  {t('finance.services.algoEngine.subtitle')}
                </p>
                <ul className="space-y-4 mb-8 flex-grow">
                  <li className="flex items-start text-sm text-gray-300">
                    <CheckCircle2 className="w-5 h-5 text-orange-500 mr-3 flex-shrink-0" />
                    {t('finance.services.algoEngine.f1')}
                  </li>
                  <li className="flex items-start text-sm text-gray-300">
                    <CheckCircle2 className="w-5 h-5 text-orange-500 mr-3 flex-shrink-0" />
                    {t('finance.services.algoEngine.f2')}
                  </li>
                  <li className="flex items-start text-sm text-gray-300">
                    <CheckCircle2 className="w-5 h-5 text-orange-500 mr-3 flex-shrink-0" />
                    {t('finance.services.algoEngine.f3')}
                  </li>
                </ul>
                <Link to={ROUTES.ALGO_ENGINE} className="block w-full">
                  <Button className="w-full bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-500 hover:to-orange-400 text-white border-0 shadow-lg shadow-orange-500/20">
                    {t('common.learnMore')} <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </AnimatedSection>

            {/* CARD 3: COPY SERVICE */}
            <AnimatedSection delay={300}>
              <div className="bg-[#151E32] border border-gray-600/30 rounded-2xl p-8 h-full flex flex-col relative group hover:border-gray-400 hover:shadow-[0_0_30px_rgba(156,163,175,0.15)] transition-all duration-300">
                <div className="absolute top-6 right-6">
                  <span className="flex items-center bg-green-500 text-white text-[10px] font-bold px-2 py-1 rounded shadow-lg">
                    <span className="w-1.5 h-1.5 bg-white rounded-full mr-1.5 animate-pulse"></span>
                    {t('common.live')}
                  </span>
                </div>
                <div className="w-14 h-14 rounded-xl bg-gray-500/10 border border-gray-500/20 flex items-center justify-center text-gray-400 mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Layers size={28} />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">{t('finance.services.copyService.title')}</h3>
                <p className="text-sm text-gray-400 mb-8 min-h-[40px]">
                  {t('finance.services.copyService.subtitle')}
                </p>
                <ul className="space-y-4 mb-8 flex-grow">
                  <li className="flex items-start text-sm text-gray-300">
                    <CheckCircle2 className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                    {t('finance.services.copyService.f1')}
                  </li>
                  <li className="flex items-start text-sm text-gray-300">
                    <CheckCircle2 className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                    {t('finance.services.copyService.f2')}
                  </li>
                  <li className="flex items-start text-sm text-gray-300">
                    <CheckCircle2 className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                    {t('finance.services.copyService.f3')}
                  </li>
                </ul>
                <Link to={ROUTES.COPY_SERVICE} className="block w-full">
                  <Button className="w-full bg-gradient-to-r from-gray-600 to-gray-500 hover:from-gray-500 hover:to-gray-400 text-white border-0 shadow-lg shadow-gray-500/20">
                    {t('common.learnMore')} <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </AnimatedSection>

          </div>
        </div>
      </section>

      {/* CONSULTATION */}
      <ConsultationSection />

      {/* 3. ROADMAP (Filtered Finance) */}
      <RoadmapSection category="finance" showFullLink={true} />

      {/* 4. Feed & Socials */}
      <section className="px-4 sm:px-6 lg:px-8 mb-24">
         <div className="max-w-7xl mx-auto">
            <AnimatedSection>
               <FeedSection />
               <div className="mt-16">
                  <SocialEcosystem />
               </div>
            </AnimatedSection>
         </div>
      </section>

      {/* Decorative Background Elements */}
      <div className="fixed top-0 left-0 w-full h-full pointer-events-none z-[-1] overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-900/10 rounded-full blur-[120px]"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-orange-900/10 rounded-full blur-[120px]"></div>
      </div>

    </div>
  );
};
